import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge, FilterChips } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, Copy, Pencil, Trash2, RotateCcw, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { title: "Mr.", full_name: "", relation_type: "", relation_name: "",
  dob: "", pan: "", aadhaar: "", phone: "", email: "", address: "" };

export default function Customers() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [custChip, setCustChip] = useState("active");
  const shownCust = custChip === "archived" ? list.filter((c) => c.archived_at) : list;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const [revealOpen, setRevealOpen] = useState(false);
  const [revealCust, setRevealCust] = useState(null);
  const [reason, setReason] = useState("");
  const [revealed, setRevealed] = useState(null);

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [resendInvite, setResendInvite] = useState(false);
  const [editBusy, setEditBusy] = useState(false);

  // Archive / Restore
  const [delOpen, setDelOpen] = useState(false);
  const [delFor, setDelFor] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const [custLoading, setCustLoading] = useState(false);
  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (includeArchived) params.include_archived = "true";
    setCustLoading(true);
    try { setList((await api.get("/customers", { params })).data); }
    finally { setCustLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, includeArchived]);

  const submit = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doReveal = async () => {
    try {
      const r = await api.post(`/customers/${revealCust.id}/reveal-pii`, { reason });
      setRevealed(r.data);
      toast.success("Revealed and audit-logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copyInvite = async (c) => {
    try {
      const r = await api.get(`/customers/${c.id}/invite-text`);
      await navigator.clipboard.writeText(r.data.text);
      toast.success(`Invite copied for ${c.full_name} — paste into WhatsApp, email or SMS`);
    } catch(e) { toast.error("Could not copy invite"); }
  };

  const openEdit = (c) => {
    setEditFor(c);
    setEditForm({
      title: c.title || "Mr.",
      full_name: c.full_name || "",
      relation_type: c.relation_type || "",
      relation_name: c.relation_name || "",
      dob: (c.dob || "").slice(0, 10),
      pan: "",       // encrypted at rest — leave blank, backend ignores empty on PATCH
      aadhaar: "",
      phone: c.phone || "",
      email: c.email || "",
      address: c.address || "",
    });
    setResendInvite(false);
    setEditOpen(true);
  };
  const submitEdit = async () => {
    setEditBusy(true);
    try {
      await api.patch(`/customers/${editFor.id}`, editForm,
                       { params: { resend_invite: resendInvite } });
      toast.success(resendInvite ? "Customer updated — invite re-sent" : "Customer updated");
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setEditBusy(false); }
  };

  const resendOnly = async (c) => {
    try {
      await api.post(`/customers/${c.id}/resend-invite`);
      toast.success(`Invite re-sent to ${c.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDelete = (c) => { setDelFor(c); setDelOpen(true); };
  const submitDelete = async () => {
    setDelBusy(true);
    try {
      const r = await api.delete(`/customers/${delFor.id}`);
      const bits = [];
      if (r.data?.plot_count) bits.push(`${r.data.plot_count} plot(s)`);
      if (r.data?.invoice_count) bits.push(`${r.data.invoice_count} invoice(s)`);
      toast.success(bits.length
        ? `Archived. Preserved ${bits.join(" · ")} in history.`
        : "Customer archived");
      setDelOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setDelBusy(false); }
  };

  const restore = async (c) => {
    try {
      await api.post(`/customers/${c.id}/restore`);
      toast.success("Customer restored");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Manage co-owners of your farmland projects. PAN and Aadhaar are encrypted at rest and masked by default."
        actions={
          <div className="flex gap-2 items-center">
            <Input data-testid="customer-search" placeholder="Search name / email / phone"
              value={q} onChange={(e)=>setQ(e.target.value)} className="w-64"/>
            {isSuper && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 cursor-pointer max-md:hidden">
                <Checkbox data-testid="include-archived"
                  checked={includeArchived}
                  onCheckedChange={(v)=>setIncludeArchived(!!v)}/>
                show archived
              </label>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="new-customer-btn" className="fab-m"><Plus className="h-4 w-4 mr-2"/>New customer</Button></DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
                <div className="grid gap-3 grid-cols-2 py-2">
                  <div>
                    <Label>Title</Label>
                    <Select value={form.title} onValueChange={(v)=>setForm({...form,title:v})}>
                      <SelectTrigger data-testid="cust-title"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mr.">Mr.</SelectItem>
                        <SelectItem value="Ms.">Ms.</SelectItem>
                        <SelectItem value="Mrs.">Mrs.</SelectItem>
                        <SelectItem value="Dr.">Dr.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Full name</Label><Input data-testid="cust-full-name" value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})}/></div>
                  <div><Label>Email</Label><Input data-testid="cust-email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}/></div>
                  <div><Label>Phone</Label><Input data-testid="cust-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/></div>
                  <div><Label>DOB</Label><Input data-testid="cust-dob" type="date" value={form.dob} onChange={(e)=>setForm({...form,dob:e.target.value})}/></div>
                  <div><Label>PAN</Label><Input data-testid="cust-pan" value={form.pan} onChange={(e)=>setForm({...form,pan:e.target.value.toUpperCase()})}/></div>
                  <div><Label>Aadhaar</Label><Input data-testid="cust-aadhaar" value={form.aadhaar} onChange={(e)=>setForm({...form,aadhaar:e.target.value})}/></div>
                  <div className="col-span-2"><Label>Address</Label><Input data-testid="cust-address" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>
                </div>
                <DialogFooter><Button data-testid="cust-save-btn" onClick={submit}>Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      {isSuper && (
        <FilterChips testId="cust-chips" value={custChip} onChange={(v) => { setCustChip(v); setIncludeArchived(v !== "active"); }} options={[
          { value: "active", label: "Active" }, { value: "archived", label: "Archived" }, { value: "all", label: "All" },
        ]}/>
      )}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Email</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Phone</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">PAN</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Aadhaar</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {shownCust.map((c) => {
              const archived = !!c.archived_at;
              return (
              <tr key={c.id} data-testid={`cust-row-${c.id}`} className={`border-t border-border ${archived ? "bg-muted/30 text-muted-foreground" : "hover:bg-muted/40"}`}>
                <td className="px-4 py-3 font-medium">
                  {c.full_name}
                  {archived && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">archived</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.email}</td>
                <td className="px-4 py-3 tabular">{c.phone || "-"}</td>
                <td className="px-4 py-3 tabular text-muted-foreground">{c.pan_masked || "-"}</td>
                <td className="px-4 py-3 tabular text-muted-foreground">{c.aadhaar_masked || "-"}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status}/></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {!archived && (
                      <Button data-testid={`invite-${c.id}`} size="icon" variant="ghost" onClick={()=>copyInvite(c)} title="Copy invite text">
                        <Copy className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {!archived && isSuper && c.email && (
                      <Button data-testid={`resend-invite-${c.id}`} size="icon" variant="ghost"
                        onClick={()=>resendOnly(c)} title="Re-send invite email">
                        <Mail className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {!archived && isSuper && (
                      <Button data-testid={`edit-${c.id}`} size="icon" variant="ghost"
                        onClick={()=>openEdit(c)} title="Edit customer">
                        <Pencil className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {isSuper && (
                      <Button data-testid={`reveal-${c.id}`} size="sm" variant="outline" onClick={()=>{setRevealCust(c);setRevealed(null);setReason("");setRevealOpen(true);}}>
                        <Eye className="h-4 w-4 mr-1"/> Reveal PII
                      </Button>
                    )}
                    {isSuper && !archived && (
                      <Button data-testid={`archive-${c.id}`} size="icon" variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={()=>openDelete(c)} title="Archive customer">
                        <Trash2 className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                    {isSuper && archived && (
                      <Button data-testid={`restore-${c.id}`} size="sm" variant="outline"
                        onClick={()=>restore(c)}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1"/>Restore
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );})}
            {shownCust.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">{custLoading ? "Loading…" : "No customers found."}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Reveal PII dialog (unchanged) */}
      <Dialog open={revealOpen} onOpenChange={setRevealOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reveal PII · {revealCust?.full_name}</DialogTitle></DialogHeader>
          {!revealed ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Provide a reason. This action is audit-logged.</p>
              <Label>Reason</Label>
              <Input data-testid="reveal-reason" value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="e.g. KYC audit for FY closing"/>
              <DialogFooter><Button data-testid="reveal-confirm" onClick={doReveal} disabled={!reason.trim()}>Reveal</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">PAN: </span><span className="tabular font-medium">{revealed.pan || "-"}</span></div>
              <div><span className="text-muted-foreground">Aadhaar: </span><span className="tabular font-medium">{revealed.aadhaar || "-"}</span></div>
              <div className="text-xs text-muted-foreground pt-2">This reveal has been recorded to the audit log.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit customer */}
      <Dialog open={editOpen} onOpenChange={(v)=>{ if (!editBusy) setEditOpen(v); }}>
        <DialogContent data-testid="cust-edit-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit customer · {editFor?.full_name}</DialogTitle>
            <DialogDescription>
              Leave PAN or Aadhaar blank to keep the existing encrypted value. Fill either to replace it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-2 py-2">
            <div>
              <Label>Title</Label>
              <Select value={editForm.title} onValueChange={(v)=>setEditForm({...editForm,title:v})}>
                <SelectTrigger data-testid="cust-edit-title"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Mrs.">Mrs.</SelectItem>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Full name</Label><Input data-testid="cust-edit-full-name" value={editForm.full_name} onChange={(e)=>setEditForm({...editForm,full_name:e.target.value})}/></div>
            <div><Label>Email</Label><Input data-testid="cust-edit-email" value={editForm.email} onChange={(e)=>setEditForm({...editForm,email:e.target.value})}/></div>
            <div><Label>Phone</Label><Input data-testid="cust-edit-phone" value={editForm.phone} onChange={(e)=>setEditForm({...editForm,phone:e.target.value})}/></div>
            <div><Label>DOB</Label><Input data-testid="cust-edit-dob" type="date" value={editForm.dob} onChange={(e)=>setEditForm({...editForm,dob:e.target.value})}/></div>
            <div><Label>PAN (leave blank to keep)</Label><Input data-testid="cust-edit-pan" value={editForm.pan} onChange={(e)=>setEditForm({...editForm,pan:e.target.value.toUpperCase()})}/></div>
            <div><Label>Aadhaar (leave blank to keep)</Label><Input data-testid="cust-edit-aadhaar" value={editForm.aadhaar} onChange={(e)=>setEditForm({...editForm,aadhaar:e.target.value})}/></div>
            <div className="col-span-2"><Label>Address</Label><Input data-testid="cust-edit-address" value={editForm.address} onChange={(e)=>setEditForm({...editForm,address:e.target.value})}/></div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-muted-foreground pt-1">
              <Checkbox data-testid="cust-edit-resend"
                checked={resendInvite}
                onCheckedChange={(v)=>setResendInvite(!!v)}/>
              Also re-send the invite email after saving
              <span className="text-xs">(auto-sent whenever email changes)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)} disabled={editBusy}>Cancel</Button>
            <Button data-testid="cust-edit-submit" onClick={submitEdit} disabled={editBusy}>
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive customer */}
      <Dialog open={delOpen} onOpenChange={(v)=>{ if (!delBusy) setDelOpen(v); }}>
        <DialogContent data-testid="cust-archive-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Archive this customer?</DialogTitle>
            <DialogDescription>
              This hides <strong>{delFor?.full_name}</strong> from the main list and disables their portal login.
              Every plot, invoice and payment stays untouched — tick "show archived" to find them again, and press Restore to bring them back.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDelOpen(false)} disabled={delBusy}>Cancel</Button>
            <Button data-testid="cust-archive-confirm" variant="destructive" onClick={submitDelete} disabled={delBusy}>
              {delBusy ? "Archiving…" : "Archive customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
